import { useState, useEffect } from "react";
import { FileText, Plus, ExternalLink, Play, Image, File, Heart, MessageCircle, Send, Trash2, Pencil, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface ContentComment {
  id: string;
  author_name: string;
  content: string;
  created_at: string;
  user_id: string;
}

interface ContentPost {
  id: string;
  author_name: string;
  title: string;
  description: string;
  link: string | null;
  content_type: string;
  created_at: string;
  user_id: string;
}

export default function Content() {
  const { user } = useAuth();
  const [contentPosts, setContentPosts] = useState<ContentPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [authorName, setAuthorName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [contentType, setContentType] = useState("video");
  const [submitting, setSubmitting] = useState(false);

  // Likes state
  const [likesCount, setLikesCount] = useState<Record<string, number>>({});
  const [userLikes, setUserLikes] = useState<Set<string>>(new Set());

  // Comments state
  const [commentsCount, setCommentsCount] = useState<Record<string, number>>({});
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [comments, setComments] = useState<Record<string, ContentComment[]>>({});
  const [newComment, setNewComment] = useState<Record<string, string>>({});
  const [commentAuthor, setCommentAuthor] = useState("");

  // Edit state
  const [editingContentId, setEditingContentId] = useState<string | null>(null);
  const [editContentData, setEditContentData] = useState({ title: "", description: "", link: "" });
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentContent, setEditCommentContent] = useState("");

  useEffect(() => {
    fetchContent();
  }, [user]);

  // Real-time subscriptions
  useEffect(() => {
    const contentChannel = supabase
      .channel('content-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'shared_content' },
        (payload) => {
          const newContent = payload.new as ContentPost;
          setContentPosts(prev => [newContent, ...prev]);
          setLikesCount(prev => ({ ...prev, [newContent.id]: 0 }));
          setCommentsCount(prev => ({ ...prev, [newContent.id]: 0 }));
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'shared_content' },
        (payload) => {
          const deletedContent = payload.old as ContentPost;
          setContentPosts(prev => prev.filter(p => p.id !== deletedContent.id));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'shared_content' },
        (payload) => {
          const updatedContent = payload.new as ContentPost;
          setContentPosts(prev => prev.map(p => p.id === updatedContent.id ? updatedContent : p));
        }
      )
      .subscribe();

    const likesChannel = supabase
      .channel('content-likes-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'content_likes' },
        (payload) => {
          const newLike = payload.new as { content_id: string; user_id: string };
          setLikesCount(prev => ({ ...prev, [newLike.content_id]: (prev[newLike.content_id] || 0) + 1 }));
          if (user && newLike.user_id === user.id) {
            setUserLikes(prev => new Set([...prev, newLike.content_id]));
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'content_likes' },
        (payload) => {
          const deletedLike = payload.old as { content_id: string; user_id: string };
          setLikesCount(prev => ({ ...prev, [deletedLike.content_id]: Math.max((prev[deletedLike.content_id] || 1) - 1, 0) }));
          if (user && deletedLike.user_id === user.id) {
            setUserLikes(prev => {
              const newSet = new Set(prev);
              newSet.delete(deletedLike.content_id);
              return newSet;
            });
          }
        }
      )
      .subscribe();

    const commentsChannel = supabase
      .channel('content-comments-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'content_comments' },
        (payload) => {
          const newComment = payload.new as ContentComment & { content_id: string };
          setCommentsCount(prev => ({ ...prev, [newComment.content_id]: (prev[newComment.content_id] || 0) + 1 }));
          setComments(prev => {
            if (prev[newComment.content_id]) {
              return { ...prev, [newComment.content_id]: [...prev[newComment.content_id], newComment] };
            }
            return prev;
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'content_comments' },
        (payload) => {
          const deletedComment = payload.old as ContentComment & { content_id: string };
          setCommentsCount(prev => ({ ...prev, [deletedComment.content_id]: Math.max((prev[deletedComment.content_id] || 1) - 1, 0) }));
          setComments(prev => {
            if (prev[deletedComment.content_id]) {
              return { ...prev, [deletedComment.content_id]: prev[deletedComment.content_id].filter(c => c.id !== deletedComment.id) };
            }
            return prev;
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'content_comments' },
        (payload) => {
          const updatedComment = payload.new as ContentComment & { content_id: string };
          setComments(prev => {
            if (prev[updatedComment.content_id]) {
              return { ...prev, [updatedComment.content_id]: prev[updatedComment.content_id].map(c => c.id === updatedComment.id ? updatedComment : c) };
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(contentChannel);
      supabase.removeChannel(likesChannel);
      supabase.removeChannel(commentsChannel);
    };
  }, [user]);

  const fetchContent = async () => {
    const { data, error } = await supabase
      .from("shared_content")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching content:", error);
    } else {
      setContentPosts(data || []);
      if (data && data.length > 0) {
        await fetchLikesAndComments(data.map(p => p.id));
      }
    }
    setLoading(false);
  };

  const fetchLikesAndComments = async (contentIds: string[]) => {
    const { data: likesData } = await supabase
      .from("content_likes")
      .select("content_id")
      .in("content_id", contentIds);

    const likeCounts: Record<string, number> = {};
    contentIds.forEach(id => likeCounts[id] = 0);
    likesData?.forEach(like => {
      likeCounts[like.content_id] = (likeCounts[like.content_id] || 0) + 1;
    });
    setLikesCount(likeCounts);

    if (user) {
      const { data: userLikesData } = await supabase
        .from("content_likes")
        .select("content_id")
        .eq("user_id", user.id)
        .in("content_id", contentIds);

      setUserLikes(new Set(userLikesData?.map(l => l.content_id) || []));
    }

    const { data: commentsData } = await supabase
      .from("content_comments")
      .select("content_id")
      .in("content_id", contentIds);

    const commentCounts: Record<string, number> = {};
    contentIds.forEach(id => commentCounts[id] = 0);
    commentsData?.forEach(comment => {
      commentCounts[comment.content_id] = (commentCounts[comment.content_id] || 0) + 1;
    });
    setCommentsCount(commentCounts);
  };

  const handleLike = async (contentId: string) => {
    if (!user) {
      toast.error("Please log in to like content");
      return;
    }

    const isLiked = userLikes.has(contentId);

    if (isLiked) {
      const { error } = await supabase
        .from("content_likes")
        .delete()
        .eq("content_id", contentId)
        .eq("user_id", user.id);

      if (!error) {
        setUserLikes(prev => {
          const newSet = new Set(prev);
          newSet.delete(contentId);
          return newSet;
        });
        setLikesCount(prev => ({ ...prev, [contentId]: (prev[contentId] || 1) - 1 }));
      }
    } else {
      const { error } = await supabase
        .from("content_likes")
        .insert({ content_id: contentId, user_id: user.id });

      if (!error) {
        setUserLikes(prev => new Set([...prev, contentId]));
        setLikesCount(prev => ({ ...prev, [contentId]: (prev[contentId] || 0) + 1 }));
      }
    }
  };

  const toggleComments = async (contentId: string) => {
    const isExpanded = expandedComments.has(contentId);

    if (isExpanded) {
      setExpandedComments(prev => {
        const newSet = new Set(prev);
        newSet.delete(contentId);
        return newSet;
      });
    } else {
      const { data } = await supabase
        .from("content_comments")
        .select("*")
        .eq("content_id", contentId)
        .order("created_at", { ascending: true });

      setComments(prev => ({ ...prev, [contentId]: data || [] }));
      setExpandedComments(prev => new Set([...prev, contentId]));
    }
  };

  const handleAddComment = async (contentId: string) => {
    if (!user) {
      toast.error("Please log in to comment");
      return;
    }

    const commentContent = newComment[contentId]?.trim();
    if (!commentContent || !commentAuthor.trim()) {
      toast.error("Please enter your name and comment");
      return;
    }

    const { data, error } = await supabase
      .from("content_comments")
      .insert({
        content_id: contentId,
        user_id: user.id,
        author_name: commentAuthor.trim(),
        content: commentContent,
      })
      .select()
      .single();

    if (error) {
      toast.error("Failed to add comment");
    } else {
      setComments(prev => ({
        ...prev,
        [contentId]: [...(prev[contentId] || []), data],
      }));
      setCommentsCount(prev => ({ ...prev, [contentId]: (prev[contentId] || 0) + 1 }));
      setNewComment(prev => ({ ...prev, [contentId]: "" }));
      toast.success("Comment added!");
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    const { error } = await supabase
      .from("shared_content")
      .delete()
      .eq("id", contentId);

    if (error) {
      toast.error("Failed to delete content");
    } else {
      setContentPosts(prev => prev.filter(p => p.id !== contentId));
      toast.success("Content deleted");
    }
  };

  const handleEditContent = (post: ContentPost) => {
    setEditingContentId(post.id);
    setEditContentData({ title: post.title, description: post.description, link: post.link || "" });
  };

  const handleSaveEditContent = async (contentId: string) => {
    if (!editContentData.title.trim() || !editContentData.description.trim()) {
      toast.error("Title and description are required");
      return;
    }

    const { error } = await supabase
      .from("shared_content")
      .update({ 
        title: editContentData.title.trim(), 
        description: editContentData.description.trim(),
        link: editContentData.link.trim() || null
      })
      .eq("id", contentId);

    if (error) {
      toast.error("Failed to update content");
    } else {
      setContentPosts(prev => prev.map(p => p.id === contentId ? { 
        ...p, 
        title: editContentData.title.trim(), 
        description: editContentData.description.trim(),
        link: editContentData.link.trim() || null
      } : p));
      setEditingContentId(null);
      setEditContentData({ title: "", description: "", link: "" });
      toast.success("Content updated");
    }
  };

  const handleDeleteComment = async (contentId: string, commentId: string) => {
    const { error } = await supabase
      .from("content_comments")
      .delete()
      .eq("id", commentId);

    if (error) {
      toast.error("Failed to delete comment");
    } else {
      setComments(prev => ({
        ...prev,
        [contentId]: prev[contentId]?.filter(c => c.id !== commentId) || [],
      }));
      setCommentsCount(prev => ({ ...prev, [contentId]: Math.max((prev[contentId] || 1) - 1, 0) }));
      toast.success("Comment deleted");
    }
  };

  const handleEditComment = (comment: ContentComment) => {
    setEditingCommentId(comment.id);
    setEditCommentContent(comment.content);
  };

  const handleSaveEditComment = async (contentId: string, commentId: string) => {
    if (!editCommentContent.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }

    const { error } = await supabase
      .from("content_comments")
      .update({ content: editCommentContent.trim() })
      .eq("id", commentId);

    if (error) {
      toast.error("Failed to update comment");
    } else {
      setComments(prev => ({
        ...prev,
        [contentId]: prev[contentId]?.map(c => c.id === commentId ? { ...c, content: editCommentContent.trim() } : c) || [],
      }));
      setEditingCommentId(null);
      setEditCommentContent("");
      toast.success("Comment updated");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!authorName.trim() || !title.trim() || !description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from("shared_content").insert({
      user_id: user.id,
      author_name: authorName.trim(),
      title: title.trim(),
      description: description.trim(),
      link: link.trim() || null,
      content_type: contentType,
    });

    if (error) {
      console.error("Error sharing content:", error);
      toast.error("Failed to share content");
    } else {
      toast.success("Content shared!");
      setAuthorName("");
      setTitle("");
      setDescription("");
      setLink("");
      setContentType("video");
      setIsOpen(false);
      fetchContent();
    }
    setSubmitting(false);
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Play className="w-8 h-8 text-primary" />;
      case "image":
        return <Image className="w-8 h-8 text-muted-foreground" />;
      default:
        return <File className="w-8 h-8 text-muted-foreground" />;
    }
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
            <FileText className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Content</h1>
            <p className="text-muted-foreground">Share your work with the community</p>
          </div>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button variant="secondary">
              <Plus className="w-4 h-4 mr-2" />
              Share Content
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share Your Content</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="authorName">Your Name</Label>
                <Input
                  id="authorName"
                  placeholder="Enter your name"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  maxLength={50}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Give your content a title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your content..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contentType">Content Type</Label>
                <Select value={contentType} onValueChange={setContentType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="document">Document/Template</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="link">Link (optional)</Label>
                <Input
                  id="link"
                  placeholder="https://..."
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  maxLength={500}
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Sharing..." : "Share Content"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Content Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl">
        {loading ? (
          <div className="glass-card p-6 text-center text-muted-foreground col-span-full">
            Loading content...
          </div>
        ) : contentPosts.length === 0 ? (
          <div className="glass-card p-6 text-center text-muted-foreground col-span-full">
            No content shared yet. Be the first to share!
          </div>
        ) : (
          contentPosts.map((post) => (
            <div key={post.id} className="glass-card overflow-hidden group">
              {/* Preview */}
              <div className="aspect-video bg-muted relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  {post.content_type === "video" ? (
                    <div className="w-14 h-14 rounded-full bg-background/80 flex items-center justify-center">
                      {getContentIcon(post.content_type)}
                    </div>
                  ) : (
                    getContentIcon(post.content_type)
                  )}
                </div>
                {user?.id === post.user_id && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEditContent(post)}
                      className="text-muted-foreground hover:text-foreground transition-colors p-2 bg-background/80 rounded-full"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="text-muted-foreground hover:text-destructive transition-colors p-2 bg-background/80 rounded-full">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Content</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this content? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteContent(post.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Author */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground text-xs font-bold">
                    {post.author_name.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm text-muted-foreground">{post.author_name}</span>
                  <span className="text-xs text-muted-foreground">
                    • {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                  </span>
                </div>

                {editingContentId === post.id ? (
                  <div className="space-y-2 mb-3">
                    <Input
                      value={editContentData.title}
                      onChange={(e) => setEditContentData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Title"
                      maxLength={100}
                      className="text-sm"
                    />
                    <Textarea
                      value={editContentData.description}
                      onChange={(e) => setEditContentData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Description"
                      rows={2}
                      maxLength={500}
                      className="text-sm"
                    />
                    <Input
                      value={editContentData.link}
                      onChange={(e) => setEditContentData(prev => ({ ...prev, link: e.target.value }))}
                      placeholder="Link (optional)"
                      maxLength={500}
                      className="text-sm"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" className="h-7 text-xs" onClick={() => handleSaveEditContent(post.id)}>
                        <Check className="w-3 h-3 mr-1" />
                        Save
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditingContentId(null); setEditContentData({ title: "", description: "", link: "" }); }}>
                        <X className="w-3 h-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {post.description}
                    </p>

                    {post.link && (
                      <Button variant="ghost" size="sm" className="w-full mb-3" asChild>
                        <a href={post.link} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Content
                        </a>
                      </Button>
                    )}
                  </>
                )}

                {/* Actions */}
                <div className="flex items-center gap-4 pt-2 border-t border-border">
                  <button
                    onClick={() => handleLike(post.id)}
                    className={`flex items-center gap-2 transition-colors ${
                      userLikes.has(post.id)
                        ? "text-red-500"
                        : "text-muted-foreground hover:text-red-500"
                    }`}
                  >
                    <Heart className={`w-4 h-4 ${userLikes.has(post.id) ? "fill-current" : ""}`} />
                    <span className="text-sm">{likesCount[post.id] || 0}</span>
                  </button>
                  <button
                    onClick={() => toggleComments(post.id)}
                    className={`flex items-center gap-2 transition-colors ${
                      expandedComments.has(post.id)
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-sm">{commentsCount[post.id] || 0}</span>
                  </button>
                </div>

                {/* Comments Section */}
                {expandedComments.has(post.id) && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                      {(comments[post.id] || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No comments yet. Be the first!</p>
                      ) : (
                        (comments[post.id] || []).map((comment) => (
                          <div key={comment.id} className="flex gap-2 group/comment">
                            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
                              {comment.author_name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium">{comment.author_name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                </span>
                                {user?.id === comment.user_id && (
                                  <div className="opacity-0 group-hover/comment:opacity-100 flex items-center gap-1 ml-auto transition-all">
                                    <button
                                      onClick={() => handleEditComment(comment)}
                                      className="text-muted-foreground hover:text-foreground"
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteComment(post.id, comment.id)}
                                      className="text-muted-foreground hover:text-destructive"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                              {editingCommentId === comment.id ? (
                                <div className="mt-1 space-y-2">
                                  <Input
                                    value={editCommentContent}
                                    onChange={(e) => setEditCommentContent(e.target.value)}
                                    maxLength={500}
                                    className="text-xs h-7"
                                  />
                                  <div className="flex gap-2">
                                    <Button size="sm" className="h-6 text-xs px-2" onClick={() => handleSaveEditComment(post.id, comment.id)}>
                                      <Check className="w-3 h-3 mr-1" />
                                      Save
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => { setEditingCommentId(null); setEditCommentContent(""); }}>
                                      <X className="w-3 h-3 mr-1" />
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-foreground">{comment.content}</p>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Add Comment */}
                    <div className="space-y-2">
                      {!commentAuthor && (
                        <Input
                          placeholder="Your name"
                          value={commentAuthor}
                          onChange={(e) => setCommentAuthor(e.target.value)}
                          className="text-sm h-8"
                          maxLength={50}
                        />
                      )}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Write a comment..."
                          value={newComment[post.id] || ""}
                          onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                          className="flex-1 text-sm h-8"
                          maxLength={500}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleAddComment(post.id);
                            }
                          }}
                        />
                        <Button
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleAddComment(post.id)}
                          disabled={!newComment[post.id]?.trim()}
                        >
                          <Send className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}